import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TriangleAlert } from 'lucide-react';
import { AdminButton, AdminCard, AdminPageHeader } from '../../ui';
import { ArenaChallenge } from '../../../../api/group/arena.api';
import {
  adminDisqualifyEntry,
  adminFinalizeChallenge,
  adminListEntries,
  adminScoreEntry,
  ArenaAdminEntry,
} from '../../../../api/group/arena-admin.api';

interface Props {
  challenge: ArenaChallenge;
  onBack: () => void;
  onFinalized: () => void;
}

const ArenaJudging: React.FC<Props> = ({ challenge, onBack, onFinalized }) => {
  const [rows, setRows] = useState<ArenaAdminEntry[]>([]);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const done = challenge.status === 'FINALIZED';

  const load = useCallback(async () => {
    try {
      const list = await adminListEntries(challenge.id);
      setRows(list);
      setScores(
        Object.fromEntries(list.map((r) => [r.teamId, r.qualityScore == null ? '' : String(r.qualityScore)]))
      );
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được danh sách đội.');
    }
  }, [challenge.id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveScore = async (teamId: string) => {
    const n = Number(scores[teamId]);
    if (scores[teamId] === '' || Number.isNaN(n) || n < 0 || n > 100) {
      toast.error('Điểm chất lượng từ 0 đến 100.');
      return;
    }
    try {
      setBusy(teamId);
      await adminScoreEntry(teamId, Math.round(n));
      toast.success('Đã lưu điểm.');
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Không lưu được điểm.');
    } finally {
      setBusy(null);
    }
  };

  const disqualify = async (teamId: string) => {
    const reason = window.prompt('Lý do loại đội:');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('Nhập lý do loại đội.');
      return;
    }
    try {
      setBusy(teamId);
      await adminDisqualifyEntry(teamId, reason.trim());
      toast.success('Đã loại đội.');
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Không loại được đội.');
    } finally {
      setBusy(null);
    }
  };

  const finalize = async () => {
    if (!window.confirm('Chốt kết quả xong không sửa được nữa. Chốt ngay?')) return;
    try {
      setBusy('finalize');
      await adminFinalizeChallenge(challenge.id);
      toast.success('Đã chốt kết quả.');
      onFinalized();
    } catch (e: any) {
      toast.error(e?.message || 'Không chốt được kết quả.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title={`Chấm: ${challenge.title}`}
        actions={
          <>
            <AdminButton variant="outline" onClick={onBack}>
              Quay lại
            </AdminButton>
            {!done && (
              <AdminButton variant="danger" disabled={busy === 'finalize'} onClick={finalize}>
                Chốt kết quả
              </AdminButton>
            )}
          </>
        }
      />
      {rows.some((r) => r.warning) && (
        <div className="adm-strip adm-strip--warning" role="status" data-testid="arena-stats-warning">
          <TriangleAlert size={20} className="adm-strip__icon" />
          Chưa lấy được số liệu tương tác, đang hiện 0.
        </div>
      )}
      <AdminCard flush>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Tên đội</th>
                <th>Thành viên</th>
                <th>Bài nộp</th>
                <th>Tương tác tạm tính</th>
                <th>Điểm chất lượng</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.teamId}>
                  <td>
                    <span style={{ fontWeight: 600, color: 'var(--adm-heading)' }}>{r.teamName}</span>
                    {r.disqualified && <div className="adm-sub" style={{ color: 'var(--adm-danger)' }}>Đã loại: {r.disqualifyReason}</div>}
                    {r.note && <div className="adm-sub">{r.note}</div>}
                  </td>
                  <td>{r.members.length}</td>
                  <td>
                    {r.postId ? (
                      <a href={`/share/post/${r.postId}`} target="_blank" rel="noopener noreferrer" className="adm-link">
                        Xem bài
                      </a>
                    ) : (
                      'Chưa nộp'
                    )}
                  </td>
                  <td>
                    {r.interactionRaw}
                    <div className="adm-sub">
                      {r.likes} thích, {r.comments} bình luận, {r.shares} chia sẻ
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        disabled={done || r.disqualified}
                        aria-label={`Điểm chất lượng ${r.teamName}`}
                        value={scores[r.teamId] ?? ''}
                        onChange={(e) => setScores((p) => ({ ...p, [r.teamId]: e.target.value }))}
                        className="adm-input"
                        style={{ width: 88 }}
                      />
                      <AdminButton
                        size={32}
                        variant="soft"
                        disabled={done || r.disqualified || busy === r.teamId}
                        onClick={() => saveScore(r.teamId)}
                      >
                        Lưu điểm
                      </AdminButton>
                    </div>
                  </td>
                  <td>
                    {!done && !r.disqualified && (
                      <div className="flex justify-end">
                        <AdminButton size={32} variant="danger-soft" disabled={busy === r.teamId} onClick={() => disqualify(r.teamId)}>
                          Loại
                        </AdminButton>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--adm-muted)' }}>
                    Chưa có đội nào.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </div>
  );
};

export default ArenaJudging;
